import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ErrorReportControllerv2Service, SendErrorReportElementRequest } from '@app/generated-api';
import { utc } from 'moment';
import { BehaviorSubject, distinctUntilChanged, filter, Observable, Subject } from 'rxjs';
import { ErrorDetails } from '../../models/error-details';

@Injectable({
    providedIn: 'root',
})
export class ErrorService {
    private readonly errorDetails: BehaviorSubject<ErrorDetails[]> = new BehaviorSubject<ErrorDetails[]>([]);
    private readonly httpErrors = new Subject<HttpErrorResponse>();
    private readonly versionError: Subject<string | null> = new Subject<string | null>();

    private readonly userMessageError: Subject<{
        title?: string | undefined;
        description: string | null;
        displayCloseButton?: boolean | undefined;
    }> = new Subject<{
        title?: string | undefined;
        description: string | null;
        displayCloseButton?: boolean | undefined;
    }>();

    private errorReport = inject(ErrorReportControllerv2Service);

    constructor() {}
    pushHttpError(httpError: HttpErrorResponse) {
        this.httpErrors.next(httpError);
    }
    getHttpErrors() {
        return this.httpErrors.asObservable();
    }
    getErrors(): ErrorDetails[] {
        return this.errorDetails.getValue();
    }

    addError(errorMessage: string, callStack: string) {
        let error: ErrorDetails = {
            createDatetime: utc(Date.now()),
            errorTitle: errorMessage,
            errorCallStack: callStack,
            url: window.location.href,
        };

        this.errorDetails.next([...this.errorDetails.getValue(), error]);
    }

    clearErrorStateService() {
        this.errorDetails.next([]);
    }

    reportBug(description: string, url: string) {
        return this.errorReport.errorReportControllerv2SendErrorReport({
            description,
            url,
            errorDetails: this.getErrors().map(
                (x) =>
                    <SendErrorReportElementRequest>{
                        url: x.url,
                        errorTitle: x.errorTitle,
                        errorCallStack: x.errorCallStack,
                        createDatetime: x.createDatetime.toISOString(),
                    }
            ),
        });
    }

    throwVersionError(errorMessage: string) {
        this.versionError.next(errorMessage);
    }

    clearVersionError() {
        this.versionError.next(null);
    }

    handleVersionError(): Observable<string> {
        return this.versionError.asObservable().pipe(distinctUntilChanged(), filter(Boolean));
    }

    throwUserMessageError(
        description: string | null,
        title?: string | undefined,
        displayCloseButton?: boolean | undefined
    ) {
        this.userMessageError.next({ title, description, displayCloseButton });
    }

    handleUserMessageError(): Observable<{
        title?: string | undefined;
        description: string | null;
        displayCloseButton?: boolean | undefined;
    }> {
        return this.userMessageError.asObservable().pipe(
            filter((error) => !!error.description),
            distinctUntilChanged(
                (previous, current) =>
                    previous.title === current.title &&
                    previous.description === current.description &&
                    previous.displayCloseButton === current.displayCloseButton
            )
        );
    }

    clearMessageError() {
        this.userMessageError.next({ title: undefined, description: null, displayCloseButton: undefined });
    }
}
